<template>
    <ui5-dialog
        ref="dialog"
        class="uses-tailwind min-w-[min(90vw,_600px)]"
        :header-text="
            title +
            (deliveryroute ? ` ${deliveryroute}` : '') +
            (originalSelectedRide
                ? ` (${rideLabel(originalSelectedRide, true)})`
                : '')
        "
        stretch="true"
    >
        <div>
            <p class="text-lg font-bold w-full mb-4">
                {{ trans('order_scheduler.sections.delivery_date') }}
            </p>
            <div class="grid grid-cols-3 gap-4">
                <div class="relative">
                    <ui5-label class="w-full">
                        {{ trans('order_scheduler.labels.delivery_date') }}
                    </ui5-label>
                    <ui5-date-picker
                        format-pattern="yyyy-MM-dd"
                        ref="deliveryDatePicker"
                        class="w-full"
                        required
                        :value="selectedDateString"
                        @change="selectedDate = moment($event.detail.value)"
                        :min-date="today.format('YYYY-MM-DD')"
                    />
                    <div class="mt-2 relative">
                        <ui5-label>
                            {{ trans('order_scheduler.labels.capacity') }}
                        </ui5-label>
                        <div>
                            <strong>{{ capacity }}</strong>
                            min.
                        </div>
                        <div
                            class="absolute inset-0 bg-white/80 flex items-center justify-center z-50"
                            v-show="!step1Loading && capacityLoading"
                        >
                            <spinner />
                        </div>
                    </div>
                    <div
                        class="absolute inset-0 bg-white/80 flex items-center justify-center z-50"
                        v-show="step1Loading"
                    >
                        <spinner />
                    </div>
                </div>
                <div class="relative">
                    <ui5-label class="w-full">
                        {{ trans('order_scheduler.labels.delivery_method') }}
                    </ui5-label>
                    <ui5-select
                        ref="deliveryMethodSelect"
                        class="w-full"
                        :value="selectedDeliveryMethod"
                        @change="
                            selectedDeliveryMethod =
                                $event.detail.selectedOption.value
                        "
                    >
                        <ui5-option
                            value=""
                            :selected="!selectedDeliveryMethod"
                        >
                            {{
                                trans('order_scheduler.delivery_methods.filter')
                            }}
                        </ui5-option>
                        <ui5-option
                            v-for="deliveryMethod in deliveryMethods"
                            :key="deliveryMethod.id"
                            :value="deliveryMethod.id"
                            :selected="
                                selectedDeliveryMethod == deliveryMethod.id
                            "
                        >
                            {{ deliveryMethod.name }}
                        </ui5-option>
                    </ui5-select>
                    <div
                        class="absolute inset-0 bg-white/80 flex items-center justify-center z-50"
                        v-show="step2Loading"
                    >
                        <spinner />
                    </div>
                </div>
                <div class="relative">
                    <ui5-label class="w-full">
                        {{ trans('order_scheduler.labels.ride') }}
                    </ui5-label>
                    <ui5-select
                        ref="rideSelect"
                        class="w-full"
                        @change="
                            selectedRideId = $event.detail.selectedOption.value
                        "
                    >
                        <ui5-option
                            value=""
                            :selected="!selectedRideId"
                        >
                            {{ trans('order_scheduler.select_a_ride') }}
                        </ui5-option>
                        <ui5-option
                            v-for="ride in filteredRides"
                            :key="ride.id"
                            :value="ride.id"
                            :selected="selectedRideId == ride.id"
                        >
                            {{ rideLabel(ride) }}
                        </ui5-option>
                    </ui5-select>
                    <div>
                        <p
                            class="text-red-700 font-bold my-2"
                            v-if="!step3Loading && filteredRides.length == 0"
                        >
                            {{
                                trans('order_scheduler.errors.ride.no_results')
                            }}
                        </p>
                        <ui5-button
                            @click="newDeliveryDate"
                            class="border border-primary mt-2"
                        >
                            {{ trans('order_scheduler.actions.add_new_ride') }}
                        </ui5-button>
                    </div>
                    <div
                        class="absolute inset-0 bg-white/80 flex items-center justify-center z-50"
                        v-show="step1Loading || step2Loading || step3Loading"
                    >
                        <spinner />
                    </div>
                </div>
            </div>
        </div>
        <div class="mt-6">
            <p class="text-lg font-bold w-full mb-4">
                {{ trans('order_scheduler.sections.production_date') }}
            </p>
            <div class="grid grid-cols-3 gap-4 relative">
                <div>
                    <ui5-label class="w-full">
                        {{ trans('order_scheduler.labels.production_date') }}
                    </ui5-label>
                    <ui5-date-picker
                        format-pattern="yyyy-MM-dd"
                        ref="productionDatePicker"
                        class="w-full"
                        required
                        :value="selectedProductionDateString"
                        @change="
                            manuallySetProductionDate = true;
                            selectedProductionDate = moment(
                                $event.detail.value
                            );
                        "
                        :min-date="today.format('YYYY-MM-DD')"
                        :max-date="selectedDateString"
                    />
                </div>
                <div class="col-span-2">
                    <production-progress-container
                        v-if="selectedProductionDate"
                        :date-string="selectedProductionDateString"
                        :workstation-type-ids="workstationTypeIds"
                    />
                </div>
                <div
                    class="absolute inset-0 bg-white/80 flex items-center justify-center z-50"
                    v-show="step4Loading"
                >
                    <spinner />
                </div>
            </div>
        </div>
        <div class="mt-6">
            <p class="text-lg font-bold w-full mb-4">
                {{ trans('order_scheduler.sections.summary') }}
            </p>
            <p
                v-html="
                    trans('order_scheduler.summary.delivery_date', {
                        date: selectedRide
                            ? moment(selectedRide.date).format('YYYY-MM-DD')
                            : trans('order_scheduler.summary.not_selected'),
                    })
                "
            ></p>
            <p
                class="mt-2"
                v-html="
                    trans('order_scheduler.summary.production_date', {
                        date:
                            selectedProductionDateString ||
                            trans('order_scheduler.summary.not_selected'),
                    })
                "
            ></p>
        </div>
        <div class="mt-5 flex items-center justify-end gap-4">
            <ui5-button
                class="border border-primary"
                @click="confirmClick"
            >
                {{ trans('order_scheduler.actions.confirm') }}
            </ui5-button>
            <ui5-button
                class="border border-primary"
                @click="close"
            >
                {{ trans('order_scheduler.actions.cancel') }}
            </ui5-button>
        </div>
    </ui5-dialog>
</template>
<script>
import moment from 'moment';
import IsDebugMixin from '../../mixins/is-debug';
import { mapActions } from 'vuex';
import Spinner from '../loader/Spinner';
import deliveryDatesApi from '../../api/deliveryDates';
import orderApi from '../../api/order';
import settingsApi from '../../api/settings';
import { showInfoMessage, showErrorMessage } from '../../dialogs';
import ProductionProgressContainer from '../production-progress/ProductionProgressContainer';

export default {
    components: { Spinner, ProductionProgressContainer },
    mixins: [IsDebugMixin],
    props: {
        title: {
            type: String,
            required: true,
        },
        newDeliveryDate: {
            type: Function,
            required: false,
        },
        onConfirm: {
            type: Function,
            required: true,
        },
        workstationTypeIds: {
            type: Array,
            required: false,
        },
        deliveryroute: {
            type: String,
            required: false,
        },
    },
    data() {
        return {
            orderId: null,
            step1Loading: false,
            step2Loading: false,
            step3Loading: false,
            step4Loading: false,
            selectedDate: null,
            selectedDeliveryMethod: null,
            selectedRideId: null,
            originalSelectedRideId: null,
            originalRideObject: null,
            deliveryMethods: [],
            capacityLoading: false,
            capacity: 0,
            selectedProductionDate: null,
            manuallySetProductionDate: false,
            productionDateOffsetInDays: 0,
        };
    },
    watch: {
        selectedDate() {
            this.fetchCapacityDebounced();
        },
        selectedRideId() {
            this.automaticallySetProductionDateIfPossible();
        },
        selectedRide() {
            this.automaticallySetProductionDateIfPossible();
        },
    },
    computed: {
        today() {
            return moment();
        },
        selectedDateString() {
            return this.selectedDate instanceof moment
                ? this.selectedDate.format('YYYY-MM-DD')
                : '';
        },
        selectedProductionDateString() {
            return this.selectedProductionDate instanceof moment
                ? this.selectedProductionDate.format('YYYY-MM-DD')
                : '';
        },
        filteredRides() {
            this.startLoadingStep(3);
            if (!this.selectedDate) {
                this.stopLoadingStep(3);
                return [];
            }

            const rides =
                this.$store.getters['deliveryDates/getSchedulePopupList'];

            const filteredRides = (rides ? rides.list || [] : [])
                .filter((ride) => {
                    return (
                        (!this.selectedDeliveryMethod ||
                            ride.deliveryMethodId ==
                                this.selectedDeliveryMethod) &&
                        this.selectedDate.isSameOrBefore(
                            moment(ride.date),
                            'day'
                        )
                    );
                })
                .sort((a, b) => {
                    return moment(a.date).diff(moment(b.date));
                });

            if (
                (!this.selectedRideId ||
                    !filteredRides.find(
                        (ride) => ride.id == this.selectedRideId
                    )) &&
                filteredRides.length > 0
            ) {
                this.selectedRideId = filteredRides[0].id;
            }

            this.stopLoadingStep(3);

            return filteredRides;
        },
        selectedRide() {
            if (!this.selectedRideId) {
                return null;
            }

            return this.filteredRides.find(
                (ride) => ride.id == this.selectedRideId
            );
        },
        originalSelectedRide() {
            if (this.originalRideObject) {
                return this.originalRideObject;
            }

            if (!this.originalSelectedRideId) {
                return null;
            }

            return this.filteredRides.find(
                (ride) => ride.id == this.originalSelectedRideId
            );
        },
    },
    methods: {
        async open(orderId, selectedRide) {
            this.validateSchedulePopup();
            this.fetchSchedulePopupList();
            this.fetchProductionDateOffset();

            this.orderId = orderId;
            if (selectedRide) {
                this.originalSelectedRideId = selectedRide.id;
                this.originalRideObject = selectedRide;
                this.selectedRideId = selectedRide.id;
                this.selectedDate = moment(selectedRide.date);
            } else {
                this.originalSelectedRideId = null;
                this.originalRideObject = null;
                this.startLoadingStep(1);
                orderApi
                    .earliestPossibleDeliveryDateForOrder(this.orderId)
                    .then((response) => {
                        this.selectedDate = moment(
                            response.data.data.earliestPossibleDeliveryDate
                        );
                    })
                    .catch((error) => {
                        this.selectedDate = moment();
                    })
                    .finally(() => {
                        this.stopLoadingStep(1);
                    });
            }

            this.automaticallySetProductionDateIfPossible();

            this.fetchDeliveryMethods();

            this.$refs.dialog.show();
        },
        close() {
            this.$refs.dialog.close();
        },
        startLoadingStep(step) {
            this[`step${step}Loading`] = true;
        },
        stopLoadingStep(step) {
            this[`step${step}Loading`] = false;
        },
        fetchDeliveryMethods() {
            this.startLoadingStep(2);
            deliveryDatesApi
                .listDeliveryMethods()
                .then((response) => {
                    this.deliveryMethods = response.data.data;
                })
                .then(() => {
                    this.stopLoadingStep(2);
                });
        },
        fetchCapacityDebounced: _.debounce(function () {
            this.fetchCapacity();
        }, 500),
        fetchCapacity() {
            this.capacityLoading = true;
            deliveryDatesApi
                .fetchCapacityForDeliveryDateDate(this.selectedDateString)
                .then((response) => {
                    this.capacity = response.data.data.capacity;
                })
                .catch((error) => {
                    this.capacity = 0;
                    if (error.response.data == 'too early') {
                        showErrorMessage(
                            this.trans('order_scheduler.errors.date.too_early')
                        );
                    } else {
                        showErrorMessage(error.response.data);
                    }
                })
                .finally(() => {
                    this.capacityLoading = false;
                });
        },
        async automaticallySetProductionDateIfPossible() {
            if (this.manuallySetProductionDate || !this.selectedRideId) {
                return;
            }

            if (!this.selectedRide) {
                return;
            }

            this.selectedProductionDate = await this.fetchClosestWorkingDay(
                moment(
                    Math.max(
                        this.today,
                        this.subtractWorkingDays(
                            moment(this.selectedRide.date),
                            this.productionDateOffsetInDays
                        )
                    )
                ),
                true,
                false
            );
        },
        subtractWorkingDays(date, days) {
            let newDate = moment(date);
            while (days > 0) {
                newDate = newDate.subtract(1, 'days');
                if (newDate.isoWeekday() < 6) {
                    days--;
                }
            }

            return newDate;
        },
        fetchProductionDateOffset() {
            this.startLoadingStep(4);
            settingsApi
                .fetchSettings(['DEFAULT_PRODUCTION_DATE_OFFSET_IN_DAYS'])
                .then((response) => {
                    this.productionDateOffsetInDays =
                        response.data.data
                            .DEFAULT_PRODUCTION_DATE_OFFSET_IN_DAYS || 0;
                })
                .catch((error) => {
                    this.productionDateOffsetInDays = 0;
                })
                .finally(() => {
                    this.stopLoadingStep(4);
                });
        },
        fetchClosestWorkingDay(date, allowBefore = true, allowAfter = true) {
            return orderApi
                .fetchClosestWorkingDay(date, allowBefore, allowAfter)
                .then((response) => {
                    return moment(response.data.data.closestWorkingDay);
                })
                .catch((error) => {
                    return date;
                });
        },
        validateProductionDateFilled() {
            if (!this.selectedProductionDate) {
                showErrorMessage(
                    this.trans(
                        'order_scheduler.errors.production_date.not_selected'
                    )
                );

                return false;
            }

            return true;
        },
        validateProductionDateOffset() {
            if (!this.selectedProductionDate || !this.selectedRide) {
                return false;
            }

            if (this.selectedProductionDate.isBefore(this.today, 'day')) {
                showErrorMessage(
                    this.trans(
                        'order_scheduler.errors.production_date.too_early'
                    )
                );

                return false;
            }

            if (
                this.selectedProductionDate.isAfter(
                    moment(this.selectedRide.date),
                    'day'
                )
            ) {
                showErrorMessage(
                    this.trans(
                        'order_scheduler.errors.production_date.too_late'
                    )
                );

                return false;
            }

            return true;
        },
        async validateProductionDateIsWorkingDay() {
            if (!this.selectedProductionDate || !this.selectedRide) {
                return false;
            }

            if (await orderApi.isWorkingDay(this.selectedProductionDate)) {
                return true;
            }

            showErrorMessage(
                this.trans(
                    'order_scheduler.errors.production_date.not_working_day'
                )
            );

            return false;
        },
        validateRideFilled() {
            if (!this.selectedRideId) {
                showErrorMessage(
                    this.trans('order_scheduler.errors.ride.not_selected')
                );

                return false;
            }

            return true;
        },
        validateRideDate() {
            if (!this.selectedRide) {
                return false;
            }

            if (moment(this.selectedRide.date).isBefore(this.today, 'day')) {
                showErrorMessage(
                    this.trans('order_scheduler.errors.ride.too_early')
                );

                return false;
            }

            return true;
        },
        confirmClick($event) {
            if (!this.validateRideFilled()) {
                return;
            }

            if (!this.validateRideDate()) {
                return;
            }

            if (!this.validateProductionDateFilled()) {
                return;
            }

            if (!this.validateProductionDateOffset()) {
                return;
            }

            if (!this.validateProductionDateIsWorkingDay()) {
                return;
            }

            this.onConfirm(
                this.selectedRide,
                this.selectedProductionDate.format('YYYY-MM-DD'),
                $event.target,
                this.orderId
            );
        },
        rideLabel(ride, short = false) {
            if (short) {
                return `${ride.key} - ${ride.description}`;
            }

            return `${ride.key} - ${ride.description} - ${
                ride.driverDetails ? ride.driverDetails.name : ''
            } - ${ride.truckDetails ? ride.truckDetails.name : ''} - ${
                ride.deliveryMethodDetails
                    ? ride.deliveryMethodDetails.description
                    : ''
            } (${ride.date})`;
        },
        ...mapActions({
            validateSchedulePopup: 'deliveryDates/validateSchedulePopup',
            fetchSchedulePopupList: 'deliveryDates/fetchSchedulePopupList',
        }),
        moment,
    },
};
</script>
